import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { premiumAPI } from '@/services/api';
import { EXPENSE_CATEGORIES } from '@/constants/unifiedCategories';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

function useQuery() {
  return new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
}

const monthsBack = (n) => dayjs().subtract(n, 'month').format('YYYY-MM');

const currency = (v) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(
    Number.isFinite(Number(v)) ? Number(v) : 0
  );

const OverviewPage = () => {
  const { refreshPremiumClaim } = useAuth();
  const [justUpgraded, setJustUpgraded] = useState(false);
  const query = useQuery();

  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [stats, setStats] = useState({ month: selectedMonth, total: 0, byCategory: {} });
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    const status = query.get('status');
    if (status === 'success') {
      // Refresh claims then clean URL and show one-time banner
      (async () => {
        await refreshPremiumClaim();
        setJustUpgraded(true);
        const url = new URL(window.location.href);
        url.searchParams.delete('status');
        window.history.replaceState({}, '', url.toString());
      })();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError('');
        const [catsRes, budgetsRes, statsRes] = await Promise.all([
          premiumAPI.listCategories(),
          premiumAPI.listBudgets(selectedMonth),
          premiumAPI.getStats(selectedMonth),
        ]);
        if (cancelled) return;
        setCategories(catsRes.categories || []);
        setBudgets(budgetsRes.budgets || []);
        setStats(statsRes || { month: selectedMonth, total: 0, byCategory: {} });
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedMonth]);

  useEffect(() => {
    let cancelled = false;
    async function loadTrend() {
      try {
        const months = Array.from({ length: 6 }).map((_, i) => monthsBack(5 - i));
        const results = await Promise.all(months.map((m) => premiumAPI.getStats(m)));
        if (cancelled) return;
        setTrend(results.map((r) => ({ month: r.month, total: r.total })));
      } catch (e) {
        if (!cancelled) console.error('Failed to load trend', e);
      }
    }
    loadTrend();
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryMap = useMemo(() => {
    const m = new Map();
    // First, add unified expense categories (as base/default)
    EXPENSE_CATEGORIES.forEach((c) => m.set(c.id, { id: c.id, name: c.name, color: c.color, icon: c.emoji }));
    // Then add user-defined categories (they can override unified ones)
    categories.forEach((c) => m.set(c.id, c));
    return m;
  }, [categories]);

  const barData = useMemo(() => {
    // Build a set of all categoryIds that appear in either budgets (with categoryId) or stats.byCategory
    const ids = new Set([
      ...Object.keys(stats.byCategory || {}),
      ...budgets.filter((b) => b.categoryId).map((b) => b.categoryId),
    ]);

    // Ensure uncategorized is present if there is spend with no category
    if (stats.byCategory?.uncategorized && !ids.has('uncategorized')) ids.add('uncategorized');

    const budgetByCat = budgets.reduce((acc, b) => {
      if (b.categoryId) acc[b.categoryId] = (acc[b.categoryId] || 0) + Number(b.limit || 0);
      return acc;
    }, {});

    return Array.from(ids).map((id) => {
      const cat = id === 'uncategorized' ? { name: 'Uncategorized', color: '#6b7280' } : categoryMap.get(id);
      const name = cat?.name || id;
      const spent = Number(stats.byCategory?.[id] || 0);
      const budget = Number(budgetByCat[id] || 0);
      return { id, name, spent, budget };
    });
  }, [stats, budgets, categoryMap]);

  const pieData = useMemo(() => {
    const entries = Object.entries(stats.byCategory || {});
    if (!entries.length) return [];
    return entries.map(([id, value]) => {
      const cat = id === 'uncategorized' ? { name: 'Uncategorized', color: '#6b7280' } : categoryMap.get(id);
      return { id, name: cat?.name || id, value: Number(value), color: cat?.color || '#93c5fd' };
    });
  }, [stats, categoryMap]);

  const monthOptions = useMemo(() => {
    // Current + previous 11 months
    return Array.from({ length: 12 }).map((_, i) => {
      const m = dayjs().subtract(i, 'month');
      return { key: m.format('YYYY-MM'), label: m.format('MMMM YYYY') };
    });
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-2">Premium Finance Overview</h1>
      <p className="text-gray-600 mb-4">Visualize your spending, budgets, and trends.</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <label className="text-sm text-gray-700">Month</label>
        <select
          className="border rounded px-3 py-2 w-full sm:w-auto"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
        >
          {monthOptions.map((m) => (
            <option key={m.key} value={m.key}>
              {m.label}
            </option>
          ))}
        </select>
        <div className="ml-auto text-sm text-gray-500">Total: {currency(stats.total || 0)}</div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-2">Spend vs Budget by Category</h2>
          <div className="h-64">
            {loading ? (
              <div className="h-full flex items-center justify-center text-gray-500">Loading…</div>
            ) : barData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="name" hide={barData.length > 8} angle={0} tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <RechartsTooltip formatter={(v) => currency(v)} />
                  <Legend />
                  <Bar dataKey="spent" name="Spent" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="budget" name="Budget" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">No data for this month.</div>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <h2 className="text-lg font-semibold mb-2">Monthly Spend Trend</h2>
          <div className="h-64">
            {trend.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis dataKey="month" tickFormatter={(m) => dayjs(m).format('MMM')} />
                  <YAxis tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                  <RechartsTooltip formatter={(v) => currency(v)} labelFormatter={(m) => dayjs(m).format('MMMM YYYY')} />
                  <Line type="monotone" dataKey="total" name="Total" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">Not enough data.</div>
            )}
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-4 lg:col-span-2">
          <h2 className="text-lg font-semibold mb-2">Spending Distribution</h2>
          <div className="h-72">
            {pieData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                    {pieData.map((entry, index) => (
                      <Cell key={`slice-${entry.id}-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(v) => currency(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">No transactions yet.</div>
            )}
          </div>
        </div>
      </div>

      {justUpgraded && (
        <div className="mt-6 p-4 rounded bg-green-50 border border-green-200 text-green-800">
          Subscription activated! Premium features unlocked.
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/premium/categories" className="block bg-white border rounded p-4 hover:bg-gray-50">
          <div className="text-lg font-semibold">Manage Categories</div>
          <div className="text-gray-600">Create and organize your spending categories.</div>
        </Link>
        <Link to="/premium/budgets" className="block bg-white border rounded p-4 hover:bg-gray-50">
          <div className="text-lg font-semibold">Manage Budgets</div>
          <div className="text-gray-600">Set monthly limits and track against them.</div>
        </Link>
        {/* Transactions link removed - functionality covered by Expense and Budget pages */}
      </div>
    </div>
  );
};

export default OverviewPage;
